import React from 'react';
import { wrapFunctionalComponent } from 'utils/framework';
import CreditCard from 'components/FrictionlessCheckout/Payment/CreditCard';
import Paypal from 'components/FrictionlessCheckout/Payment/PaymentMethodList/Paypal';
import UseAGiftCard from 'components/FrictionlessCheckout/PaymentReleaseFour/GiftCards/UseAGiftCard';
import GiftCardItem from 'components/FrictionlessCheckout/PaymentReleaseFour/GiftCards/GiftCardItem';
import { colors, fontSizes } from 'style/config';
import ErrorMsg from 'components/ErrorMsg';
import OrderUtils from 'utils/Order';
import {
    Box, Divider, Text, Grid
} from 'components/ui';
import AccountCredit from 'components/FrictionlessCheckout/PaymentReleaseFour/PaymentMethodList/AccountCredit/AccountCredit';
import PaymentDetails from 'components/FrictionlessCheckout/PaymentReleaseFour/PaymentDetails';
import AlternatePaymentButtons from 'components/FrictionlessCheckout/PaymentReleaseFour/PaymentMethodList/AlternatePaymentButtons/AlternatePaymentButtons';
import GcPurchaseUnavailablePayments from 'components/FrictionlessCheckout/PaymentReleaseFour/PaymentsDisplay/GcPurchaseUnavailablePayments';
import { PAYMENT_TYPES } from 'constants/RwdBasket';
import agentAwareUtils from 'utils/AgentAware';

// eslint-disable-next-line complexity
const PaymentsDisplay = ({
    isPaymentComplete,
    creditCardPaymentGroup,
    localization,
    isPaypalSelected,
    payPalPaymentGroup,
    venmoUsername,
    storeCredits,
    giftCardPaymentGroups,
    canRedeemGiftCards,
    alternateMethodSelected,
    alternatePaymentName,
    checkoutEnabled,
    installmentValue,
    showError,
    isEfullfillmentOrder,
    shouldRenderGiftCardSection,
    isAfterpayEnabledForThisOrder,
    isKlarnaEnabledForThisOrder,
    isPayPalEnabled,
    isPayPalPayLaterEligible,
    isAfterpayEnabledForThisProfile,
    handleAfterPayExpand,
    handleKlarnaExpand,
    handlePayPalClick,
    handlePazeExpand,
    onGiftCardFormVisibilityChange,
    isGiftCardFormOpen,
    showGiftCardAdditionalPaymentRequired,
    giftCardAmountEqualsOrderTotal,
    paymentGroupTypes = [],
    isSubscriptionOrder,
    isZeroDollarOrderWithCVVValidation,
    isFullyCoveredByAccountCredit,
    isGiftCardPurchase,
    isPayPalCheckoutEnabledForLocale,
    onAlternatePaymentDetails
}) => {
    const hasRenderableCreditCard = Boolean(
        creditCardPaymentGroup?.cardNumber && creditCardPaymentGroup?.expirationMonth && creditCardPaymentGroup?.expirationYear
    );
    const hasNonGiftCardPaymentGroupInOrder = paymentGroupTypes.some(type => type !== 'GiftCardPaymentGroup');
    const hasStandardPaymentApplied = creditCardPaymentGroup || hasNonGiftCardPaymentGroupInOrder;
    // Alternate payment methods (Afterpay, Klarna, Venmo, Paze) can be shown when selected
    // even before the backend order is updated, as they complete during the checkout redirect
    const hasAlternatePaymentSelected = alternateMethodSelected;
    const hasNonGiftCardNonCreditPaymentAdded = hasStandardPaymentApplied || hasAlternatePaymentSelected || isPaypalSelected;
    // Account Credit is not counted as a payment method for BNPL visibility logic
    // because it's a discount/credit that partially covers order, not a full payment method
    const totalAppliedPaymentMethodsCount = (hasNonGiftCardNonCreditPaymentAdded ? 1 : 0) + giftCardPaymentGroups.length;
    const hasTwoAppliedPaymentMethods = totalAppliedPaymentMethodsCount >= 2;
    const shouldShowPazeGiftCardMessage = alternatePaymentName === PAYMENT_TYPES.PAY_PAZE && alternateMethodSelected;
    const shouldShowGiftCardAdditionalPaymentWarning =
        showGiftCardAdditionalPaymentRequired && giftCardPaymentGroups.length > 0 && !hasNonGiftCardNonCreditPaymentAdded;
    // When account credit covers full order, hide everything except account credit (unless subscription order)
    const shouldShowPrimaryPaymentSummary = isSubscriptionOrder || (!giftCardAmountEqualsOrderTotal && !isFullyCoveredByAccountCredit);
    const isGiftCardOnlyOrder = OrderUtils.isGiftCardOnly();
    const shouldShowUseGiftCardSection =
        isGiftCardPurchase ||
        (!giftCardAmountEqualsOrderTotal &&
            ((!canRedeemGiftCards && !isEfullfillmentOrder) || shouldRenderGiftCardSection) &&
            !isZeroDollarOrderWithCVVValidation &&
            !OrderUtils.isZeroCheckout() &&
            (!isFullyCoveredByAccountCredit || isSubscriptionOrder) &&
            !isGiftCardOnlyOrder);
    const shouldShowAlternatePaymentButtons =
        !isGiftCardFormOpen &&
        !hasTwoAppliedPaymentMethods &&
        alternatePaymentName !== PAYMENT_TYPES.PAY_AFTERPAY &&
        alternatePaymentName !== PAYMENT_TYPES.PAY_KLARNA &&
        !isFullyCoveredByAccountCredit &&
        !isGiftCardPurchase;
    const shouldShowAlternatePaymentEdit = alternatePaymentName === PAYMENT_TYPES.PAY_KLARNA;
    const isPayPalOrVenmoSelected = alternatePaymentName === PAYMENT_TYPES.PAY_PAYPAL || alternatePaymentName === PAYMENT_TYPES.PAY_VENMO;

    const handleEditAlternatePayment = () => {
        const handlers = {
            [PAYMENT_TYPES.PAY_KLARNA]: handleKlarnaExpand,
            [PAYMENT_TYPES.PAY_AFTERPAY]: handleAfterPayExpand,
            [PAYMENT_TYPES.PAY_PAZE]: handlePazeExpand
        };

        const handler = handlers[alternatePaymentName];
        handler && handler();
    };

    return (
        <>
            <Box paddingX={[4, 5]}>
                {/* Account credit displayed first, before other payment methods */}
                {storeCredits?.map((storeCredit, index) => (
                    <React.Fragment key={`storeCredits_${index.toString()}`}>
                        {index !== 0 && (
                            <Divider
                                marginTop={[3, 4]}
                                marginBottom={[3, 4]}
                                color={colors.lightGray}
                                width='100%'
                                marginX={[-4, -5]}
                            />
                        )}
                        <AccountCredit
                            withCheckbox={false}
                            storeCredit={storeCredit}
                            locales={{
                                accountCreditAltText: localization.accountCredit,
                                storeCreditApplied: localization.accountCredit,
                                accountCreditApplied: localization.accountCreditApplied,
                                expires: localization.expires
                            }}
                            marginTop={index === 0 ? 0 : [3, 4]}
                        />
                    </React.Fragment>
                ))}
                {/* Divider between account credit and other payment methods */}
                {storeCredits?.length > 0 && hasNonGiftCardNonCreditPaymentAdded && (
                    <Divider
                        marginTop={[3, 4]}
                        marginBottom={[3, 4]}
                        color={colors.lightGray}
                        width='100%'
                        marginX={[-4, -5]}
                    />
                )}
                {/* Standard payment methods (Credit Card, PayPal) */}
                {!alternateMethodSelected && shouldShowPrimaryPaymentSummary && (
                    <>
                        {isPaymentComplete && showError && (
                            <ErrorMsg
                                children={localization.verifyCVV}
                                fontSize={fontSizes.base}
                            />
                        )}
                        {hasRenderableCreditCard && !(isPaypalSelected && isPayPalCheckoutEnabledForLocale) && (
                            <CreditCard creditCard={creditCardPaymentGroup} />
                        )}
                        {isPaypalSelected && (!hasRenderableCreditCard || isPayPalCheckoutEnabledForLocale) && (
                            <Paypal
                                payPalPaymentGroup={payPalPaymentGroup}
                                isPayPalCheckoutEnabledForLocale={isPayPalCheckoutEnabledForLocale}
                                isPayPalSelected={isPaypalSelected}
                            />
                        )}
                    </>
                )}
                {alternateMethodSelected && shouldShowPrimaryPaymentSummary && (
                    <Grid
                        columns='1fr auto'
                        gap={3}
                        alignItems='flex-start'
                    >
                        <PaymentDetails
                            defaultPayment
                            paymentName={alternatePaymentName}
                            selected
                            checkoutEnabled={checkoutEnabled}
                            installmentValue={installmentValue}
                            isSaveState
                            username={venmoUsername}
                        />
                        {shouldShowAlternatePaymentEdit && (
                            <Text
                                fontSize='base'
                                fontWeight='normal'
                                textDecoration='underline'
                                color='blue'
                                css={{ cursor: 'pointer' }}
                                onClick={handleEditAlternatePayment}
                                data-at={Sephora.debug.dataAt('edit_alternate_payment')}
                            >
                                {localization.edit}
                            </Text>
                        )}
                    </Grid>
                )}
                {!canRedeemGiftCards &&
                    giftCardPaymentGroups?.map((giftCard, index) => (
                        <GiftCardItem
                            key={giftCard?.paymentGroup?.paymentGroupId || `giftCard_${index}`}
                            giftCard={giftCard.paymentGroup}
                            hasDivider={shouldShowPrimaryPaymentSummary || storeCredits?.length > 0 || index > 0}
                        />
                    ))}
                {!canRedeemGiftCards && shouldShowGiftCardAdditionalPaymentWarning && (
                    <Text
                        is='p'
                        color='red'
                        marginTop={[2, 3]}
                        lineHeight='tight'
                    >
                        {localization.giftCardAdditionalPaymentRequired}
                    </Text>
                )}
            </Box>

            {shouldShowAlternatePaymentButtons && (
                <div className={agentAwareUtils.applyHideAgentAwareClass()}>
                    <AlternatePaymentButtons
                        isAfterpayEnabledForThisOrder={isAfterpayEnabledForThisOrder}
                        isKlarnaEnabledForThisOrder={isKlarnaEnabledForThisOrder}
                        isPayPalEnabled={isPayPalEnabled}
                        isPayPalPayLaterEligible={isPayPalPayLaterEligible}
                        isAfterpayEnabledForThisProfile={isAfterpayEnabledForThisProfile}
                        installmentValue={installmentValue}
                        localization={localization}
                        handleAfterPayExpand={handleAfterPayExpand}
                        handleKlarnaExpand={handleKlarnaExpand}
                        handlePayPalClick={handlePayPalClick}
                        giftCardAmountEqualsOrderTotal={giftCardAmountEqualsOrderTotal}
                    />
                </div>
            )}
            {shouldShowUseGiftCardSection && (
                <>
                    <Divider
                        marginTop={[3, 4]}
                        color={colors.lightGray}
                    />
                    {giftCardPaymentGroups.length >= 2 ? (
                        <Text
                            is='p'
                            children={localization.maxGiftCards}
                            color='gray'
                            fontSize='sm'
                            marginX={[4, 5]}
                            lineHeight='tight'
                            marginTop={[3, 4]}
                        />
                    ) : alternateMethodSelected && !isPayPalOrVenmoSelected ? (
                        <Text
                            is='p'
                            children={localization.giftCardsNotCombinableWithThisPayment}
                            color='gray'
                            fontSize='sm'
                            marginX={[4, 5]}
                            lineHeight='tight'
                            marginTop={[3, 4]}
                        />
                    ) : isGiftCardOnlyOrder ? (
                        <Text
                            is='p'
                            children={localization.gcNotAvailableWhenPurchasingGiftCard}
                            color='gray'
                            fontSize='sm'
                            marginX={[4, 5]}
                            lineHeight='tight'
                            marginTop={[3, 4]}
                        />
                    ) : (
                        <UseAGiftCard onFormVisibilityChange={onGiftCardFormVisibilityChange} />
                    )}
                </>
            )}
            {isGiftCardPurchase && !isGiftCardFormOpen && (
                <GcPurchaseUnavailablePayments
                    isPayPalEnabled={isPayPalEnabled}
                    isPayPalPayLaterEligible={isPayPalPayLaterEligible}
                    isKlarnaEnabledForThisOrder={isKlarnaEnabledForThisOrder}
                    isAfterpayEnabledForThisOrder={isAfterpayEnabledForThisOrder}
                    isAfterpayEnabledForThisProfile={isAfterpayEnabledForThisProfile}
                    localization={localization}
                    onAlternatePaymentDetails={onAlternatePaymentDetails}
                />
            )}
            {(shouldShowPazeGiftCardMessage || (canRedeemGiftCards && !shouldRenderGiftCardSection)) && (
                <>
                    <Divider
                        marginTop={[3, 4]}
                        color={colors.lightGray}
                    />
                    <Text
                        is='p'
                        children={localization.giftCardsNotCombinable}
                        color='gray'
                        fontSize='sm'
                        marginX={[4, 5]}
                        lineHeight='tight'
                        marginTop={4}
                    />
                </>
            )}
        </>
    );
};

export default wrapFunctionalComponent(PaymentsDisplay, 'PaymentsDisplay');
